<template>
  <button :class="`button ${disabled ? 'disable' : ''} ${loading ? 'hide-text' : ''} ${icon ? 'has-icon' : ''}`" @click="emit">
    <i :class="`icon-${icon}`" v-if="icon" />
    <span class="loading" v-if="loading"></span>
    <slot />
    <div class="badge" v-if="badge > 0">{{ badge }}</div>
  </button>
</template>

<script>
export default {
  props: {
    icon: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    badge: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    /**
     * Emit clickevent
     * @param {Object} event
     */
    emit(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.button {
  background: var(--color-cta-blue);
  border-radius: 16px;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
  min-height: 32px;
  padding: 0 16px;
  justify-content: center;
  transition: opacity .3s;
  position: relative;

  &.small {
    font-size: 13px;
    padding: 0 8px;
    line-height: 20px;

    i {
      font-size: 20px;
    }
  }

  &.medium {
    font-size: 14px;
    padding: 0 8px;
    line-height: 20px;

    i {
      font-size: 24px;
    }
  }

  &.has-icon {
    padding-left: 12px;
  }

  i {
    font-size: 25px;
    margin-right: 9px;

    &:before {
      line-height: unset;
    }

    &.spin {
      transform: translateZ(0);
      animation: spinner 1.1s infinite linear;
    }
  }

  &.large {
    border-radius: 18px;
    font-size: 14px;
    min-height: 36px;
    padding: 0 24px;
  }

  &.underline {
    text-decoration: underline;
  }

  &.only-icon {
    i {
      margin-right: 0;
    }

    .badge {
      position: absolute;
      height: 16px;
      width: 16px;
      font-size: 12px;
      line-height: 16px;
      top: 4px;
      right: 10px;
    }
  }

  .badge {
    height: 20px;
    width: 20px;
    border-radius: 10px;
    margin-left: 7px;
    background-color: white;
    color: var(--color-cta-blue);
  }

  &.bold {
    font-weight: 500;
  }

  &.only-text {
    background: transparent;
    border-color: transparent;
    font-weight: 500;

    &:not(.keep-hover) {
      &:hover {
        cursor: default;
      }
    }

    &.add-hover {
      &:hover {
        cursor: pointer !important;
      }
    }

    &.keep-hover {
      &:hover {
        background-color: #EEF5FF;
      }
    }

    &.black {
      color: black;
    }

    &.blue {
      color: var(--color-cta-blue);

      .badge {
        background-color: var(--color-cta-blue);
        color: white;
      }
    }

    &.red {
      color: var(--color-cta-red);
    }
  }

  &.outline {
    background: transparent;
    border: 1px solid var(--color-cta-blue);
    color: var(--color-cta-blue);

    &.gray {
      border-color: rgba(0, 0, 0, .2);
      color: black;
    }

    &.white {
      border-color: white;
      color: white;
    }

    &.red {
      border-color: rgba(#EB4646, 1);
      color: #EB4646;

      &.hide-text {
        color: transparent;

        .loading {
          border-top: 2px solid rgba(255, 255, 255, 0.2);
          border-right: 2px solid rgba(255, 255, 255, 0.2);
          border-bottom: 2px solid rgba(255, 255, 255, 0.2);
          border-left: 2px solid #EB4646;
        }
      }
    }
  }

  &.disable {
    opacity: .5;
    pointer-events: none;
  }

  &.white:not(.outline) {
    background: white;
    color: var(--color-cta-blue);
  }

  &.small-white {
    background: #FBFCFF;
    color: var(--color-cta-blue);
    box-shadow: 0 1px 6px 0 rgba(177,168,140,0.20), 0 1px 1px 0 rgba(177,168,140,0.20);
    border-radius: 4px !important;
    padding: 9px 10px 7px 10px !important;
    font-weight: 500;
    min-height: auto !important;
    font-size: 14px !important;

    i {
      font-size: 28px;
      margin-right: 5px;
    }
  }

  &.green:not(.outline) {
    background-color: var(--color-cta-green);
    color: white;
  }

  &.gray:not(.outline) {
    background-color: var(--color-cta-gray);
    color: white;
  }

  &.red:not(.outline):not(.only-text) {
    background: var(--color-cta-red);
    &:not(.hide-text) {
      color: white;
    }
  }

  &.live-red:not(.outline) {
    background: var(--color-live);
    &:not(.hide-text) {
      color: white;
    }
  }

  @media(min-width: 768px) {
    &:not(.small) {
      border-radius: 20px;
      font-size: 15px;
      min-height: 42px;
      padding: 0 24px;

      &.has-icon:not(.no-side-padding) {
        padding-left: 16px;
      }
    }

    &.large {
      border-radius: 18px;
      font-size: 14px;
      min-height: 36px;
      padding: 0 24px;

      &.has-icon {
        padding-left: 20px;
      }
    }
  }

  &.justify-left {
    justify-content: flex-start;
  }

  &.no-wrap {
    white-space: nowrap;
  }

  &.with-input {
    border-radius: 0 5px 5px 0;
  }

  &.hide-text {
    color: transparent;
  }

  &.no-side-padding {
    padding-left: 0;
    padding-right: 0;
  }

  &.with-extra-side-padding {
    padding-left: 10px;
    padding-right: 10px;
  }

  &.extra-sidepadding {
    padding-left: 30px;
    padding-right: 30px;
  }

  &.padding {
    padding: 10px;
  }

  &.no-padding {
    padding: 0;
  }

  &.no-border-radius {
    border-radius: 0;
  }

  &.minimal-border-radius {
    border-radius: 5px;
  }
}

.loading, .loading:after {
  border-radius: 50%;
  width: 18px;
  height: 18px;
}
.loading {
  margin-right: 9px;
  font-size: 10px;
  position: absolute;
  text-indent: -9999em;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  border-right: 2px solid rgba(255, 255, 255, 0.2);
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  border-left: 2px solid #ffffff;
  transform: translateZ(0);
  animation: spinner 1.1s infinite linear;
  left: calc(50% - 9px);
  top: calc(50% - 9px);
}

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

</style>
